import { retry } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

/**
 * DF-0257 — sur un réseau qui cale, une connexion TCP peut être acceptée sans
 * qu'aucun octet ne suive : le `fetch` reste `pending` pour toujours et l'écran
 * de chargement ne part jamais (constaté 25 s en salle, sorti seulement par un
 * rechargement manuel). Un `fetchBaseQuery` sans `timeout` n'a aucun plafond.
 *
 * Plafond par tentative, puis UNE seule reprise : la reprise ouvre une
 * connexion neuve, ce qui est exactement ce qui a débloqué le cas de prod.
 * Budget total avant l'écran de reprise ≈ LOAD_TIMEOUT_MS × (LOAD_MAX_RETRIES + 1).
 */
export const LOAD_TIMEOUT_MS = 8000;
export const LOAD_MAX_RETRIES = 1;

/**
 * Une panne de transport : rien n'a été reçu, donc rejouer est sans risque.
 * Tout le reste (403 licence, 404 restaurant, 5xx) est une réponse du serveur
 * et garde son sens.
 */
export function isTransportError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const status = (error as FetchBaseQueryError).status;
  return status === 'TIMEOUT_ERROR' || status === 'FETCH_ERROR';
}

/**
 * Enveloppe un `fetchBaseQuery` : plafond de temps + une reprise, **sur les
 * `query` uniquement**. Les mutations sont laissées intactes — une commande
 * partie mais sans réponse ne doit être ni coupée ni rejouée à l'aveugle (le
 * seul plafond de mutation du dépôt, `POST_ROUND_TIMEOUT_MS`, est posé à la
 * main avec sa clé d'idempotence).
 */
export function withLoadResilience(
  baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  const capped: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = (
    args,
    api,
    extraOptions,
  ) => {
    if (api.type !== 'query') return baseQuery(args, api, extraOptions);
    const withTimeout: FetchArgs =
      typeof args === 'string'
        ? { url: args, timeout: LOAD_TIMEOUT_MS }
        : { timeout: LOAD_TIMEOUT_MS, ...args };
    return baseQuery(withTimeout, api, extraOptions);
  };

  return retry(capped, {
    // `maxRetries` est ignoré dès qu'on fournit `retryCondition` : le compteur
    // est donc tenu ici.
    retryCondition: (error, _args, { attempt, baseQueryApi }) =>
      baseQueryApi.type === 'query' && attempt <= LOAD_MAX_RETRIES && isTransportError(error),
  });
}
